import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EditorItemKey, menuEditorActions, menuEditorSelectors } from "features/editor/editorSlice";
import { AppState } from "lib/redux/store";
import { equals } from "ramda";
import { isContainerNode, LabelDocument, LabelItem, TemplateNodeId } from "./LabelDocumentModel";
import { applyVariation, findContainerNodeId, findNodeById } from "./labelEditorUtils";

function createNewLabelItem(document: LabelDocument): LabelItem {
    return {
        type: "label",
        templateData: applyVariation(
            document.template.templateData,
            document.template.variations[0],
            document.template.variations[0]
        ),
        variation: {...document.template.variations[0]}
    }
}

const addNewLabelItem = createAsyncThunk(
    "addNewLabelItem",
    async (_: void, {dispatch, getState}) => {
        const document = menuEditorSelectors.document(getState() as AppState);
        const item = createNewLabelItem(document as LabelDocument);
        dispatch(menuEditorActions.addItems({items: [item]}))
    }
)


interface LabelEditorState {
    itemKey: EditorItemKey|null;
    activeNodeId: TemplateNodeId|null;
}

const initialState = {} as LabelEditorState;


const labelEditorSlice = createSlice({
    name: 'labelEditor',
    initialState,
    reducers: {
        setItemKey: (state, action: PayloadAction<EditorItemKey|null>) => {
            state.activeNodeId = null;
            state.itemKey = action.payload;
        },
        setActiveNodeId: (state, action: PayloadAction<TemplateNodeId|null>) => {
            state.activeNodeId = action.payload;
        }
    }
});

export const labelEditorReducer = labelEditorSlice.reducer;

export const labelEditorSelectors = {
    itemKey: (state: AppState) => state.labelEditor.itemKey,
    activeNodeId: (state: AppState) => state.labelEditor.activeNodeId,
}

export const labelEditorActions = {
    addNewLabelItem,
    init: createAsyncThunk("labelEditor/init", async (_: void, {getState, dispatch}) => {
        const rootState = getState() as AppState;
        const focusedItem = menuEditorSelectors.focusedItem(rootState);
        if (!focusedItem) {
            dispatch(labelEditorSlice.actions.setItemKey(null));
            return;
        }
        const currentFocusedItemKey = labelEditorSelectors.itemKey(rootState);
        if (equals(focusedItem.itemKey, currentFocusedItemKey)) {
            return;
        }
        await dispatch(labelEditorSlice.actions.setItemKey(focusedItem.itemKey));
        const containerNodeId = findContainerNodeId((focusedItem.menuItem as LabelItem).templateData.document);

        if (containerNodeId !== null) {
            await dispatch(labelEditorSlice.actions.setActiveNodeId(containerNodeId));
        }
    }),
    activateNodeById: createAsyncThunk(
        "labelEditor/activateNodeById", 
        async (nodeId: TemplateNodeId, {dispatch}) => {
            dispatch(labelEditorSlice.actions.setActiveNodeId(nodeId));
        }
    ),
    unfocusActiveItem: createAsyncThunk(
        "labelEditor/unfocusActiveItem",
        async (_: void, {dispatch, getState}) => {
            const unfocus = () => dispatch(menuEditorActions.setFocusedItem(null));
            const rootState = getState() as AppState;
            const focusedItem = menuEditorSelectors.focusedItem(rootState);
            if (!focusedItem) {
                unfocus();
                return;
            }
            const activeNodeId = labelEditorSelectors.activeNodeId(rootState);
            if (!activeNodeId) {
                unfocus();
                return;
            }
            const doc = (focusedItem.menuItem as LabelItem).templateData.document;
            const activeNode = findNodeById(doc, activeNodeId);
            if (!activeNode || isContainerNode(activeNode)) {
                unfocus();
                return;
            }
            const containerNodeId = findContainerNodeId((focusedItem.menuItem as LabelItem).templateData.document);
            if (containerNodeId !== null) {
                await dispatch(labelEditorSlice.actions.setActiveNodeId(containerNodeId));
                return;
            }
            unfocus();
        }
    )
}

